/**
 * AI 提示词指令 API 服务
 */
import apiClient from './client';
import type {
  AIPromptCommandCreate,
  AIPromptCommandDetail,
  AIPromptCommandListItem,
  AIPromptCommandMenuData,
  AIPromptCommandOrderItem,
  AIPromptCommandTag,
  AIPromptCommandTagBatchUpdate,
  AIPromptCommandUpdate,
} from '@/types/ai-prompt-command';

/** 列表查询参数 */
export interface AIPromptCommandListParams {
  outputType?: number;
  usageScope?: string;
  orgId?: string | null;
  tagId?: string | null;
  name?: string;
  page?: number;
  pageSize?: number;
}

export interface AIPromptCommandMenuParams {
  outputType?: number;
  activeUsageScope?: string;
  activeOrgId?: string | null;
  orgId?: string | null;
  projectId?: string | null;
}

/** 分页列表响应 */
export interface AIPromptCommandListResponse {
  items: AIPromptCommandListItem[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  outputTypeCounts: Record<string, number>;
}

export const aiPromptCommandsApi = {
  /**
   * 查询指令列表（分页）
   * GET /api/v1/ai-prompt-commands
   */
  async list(params?: AIPromptCommandListParams): Promise<AIPromptCommandListResponse> {
    return apiClient.get('/ai-prompt-commands', { params }) as Promise<AIPromptCommandListResponse>;
  },

  /**
   * 获取菜单数据（按输出类型分组）
   * GET /api/v1/ai-prompt-commands/commands
   */
  async menu(params?: AIPromptCommandMenuParams): Promise<AIPromptCommandMenuData> {
    return apiClient.get('/ai-prompt-commands/commands', {
      params: {
        ...(params?.outputType !== undefined ? { outputType: params.outputType } : {}),
        ...(params?.activeUsageScope ? { activeUsageScope: params.activeUsageScope } : {}),
        ...(params?.orgId ? { orgId: params.orgId } : {}),
        ...(params?.projectId ? { projectId: params.projectId } : {}),
      },
    }) as Promise<AIPromptCommandMenuData>;
  },

  /**
   * 获取指令详情
   * GET /api/v1/ai-prompt-commands/{id}
   */
  async getById(promptId: string): Promise<AIPromptCommandDetail> {
    return apiClient.get(`/ai-prompt-commands/${promptId}`) as Promise<AIPromptCommandDetail>;
  },

  /**
   * 创建指令
   * POST /api/v1/ai-prompt-commands
   */
  async create(data: AIPromptCommandCreate): Promise<AIPromptCommandDetail> {
    return apiClient.post('/ai-prompt-commands', data) as Promise<AIPromptCommandDetail>;
  },

  async listTags(params?: {
    orgId?: string | null;
    outputType?: number;
  }): Promise<AIPromptCommandTag[]> {
    return apiClient.get('/ai-prompt-commands/tags', {
      params: {
        ...(params?.orgId ? { orgId: params.orgId } : {}),
        ...(params?.outputType !== undefined ? { outputType: params.outputType } : {}),
      },
    }) as Promise<AIPromptCommandTag[]>;
  },

  async createTag(data: {
    name: string;
    color?: string | null;
    orderIndex?: number;
    orgId?: string | null;
  }): Promise<AIPromptCommandTag> {
    return apiClient.post('/ai-prompt-commands/tags', data) as Promise<AIPromptCommandTag>;
  },

  async updateTag(
    tagId: string,
    data: { name?: string; color?: string | null; orderIndex?: number },
    options?: { orgId?: string | null }
  ): Promise<AIPromptCommandTag> {
    return apiClient.patch(`/ai-prompt-commands/tags/${tagId}`, data, {
      params: options?.orgId ? { orgId: options.orgId } : undefined,
    }) as Promise<AIPromptCommandTag>;
  },

  async deleteTag(tagId: string, options?: { orgId?: string | null }): Promise<void> {
    await apiClient.delete(`/ai-prompt-commands/tags/${tagId}`, {
      params: options?.orgId ? { orgId: options.orgId } : undefined,
    });
  },

  async assignTags(data: AIPromptCommandTagBatchUpdate): Promise<void> {
    await apiClient.post(
      '/ai-prompt-commands/tags/assign',
      {
        promptIds: data.promptIds,
        tagIds: data.tagIds,
      },
      {
        params: data.orgId ? { orgId: data.orgId } : undefined,
      }
    );
  },

  async removeTags(data: AIPromptCommandTagBatchUpdate): Promise<void> {
    await apiClient.post(
      '/ai-prompt-commands/tags/remove',
      {
        promptIds: data.promptIds,
        tagIds: data.tagIds,
      },
      {
        params: data.orgId ? { orgId: data.orgId } : undefined,
      }
    );
  },

  /**
   * 更新指令
   * PATCH /api/v1/ai-prompt-commands/{id}
   */
  async update(
    promptId: string,
    data: AIPromptCommandUpdate,
    options?: { orgId?: string | null }
  ): Promise<AIPromptCommandDetail> {
    return apiClient.patch(`/ai-prompt-commands/${promptId}`, data, {
      params: options?.orgId ? { orgId: options.orgId } : undefined,
    }) as Promise<AIPromptCommandDetail>;
  },

  /**
   * 批量更新排序
   * PATCH /api/v1/ai-prompt-commands/order
   */
  async reorder(
    items: AIPromptCommandOrderItem[],
    options?: { orgId?: string | null }
  ): Promise<AIPromptCommandListResponse> {
    return apiClient.patch(
      '/ai-prompt-commands/order',
      { items },
      {
        params: options?.orgId ? { orgId: options.orgId } : undefined,
      }
    ) as Promise<AIPromptCommandListResponse>;
  },

  /**
   * 设置置顶状态
   * PATCH /api/v1/ai-prompt-commands/{id}/pin
   */
  async setPinned(
    promptId: string,
    isPinned: boolean,
    options?: { orgId?: string | null }
  ): Promise<AIPromptCommandDetail> {
    return apiClient.patch(
      `/ai-prompt-commands/${promptId}/pin`,
      { isPinned },
      {
        params: options?.orgId ? { orgId: options.orgId } : undefined,
      }
    ) as Promise<AIPromptCommandDetail>;
  },

  /**
   * 删除指令
   * DELETE /api/v1/ai-prompt-commands/{id}
   */
  async delete(promptId: string, options?: { orgId?: string | null }): Promise<void> {
    await apiClient.delete(`/ai-prompt-commands/${promptId}`, {
      params: options?.orgId ? { orgId: options.orgId } : undefined,
    });
  },
};
